// @flow
import * as React from 'react';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';
import * as SS from 'styled-system';
import { BodySmall } from 'components/Text';
import Box from 'components/Box';
import PageToolbar from 'components/PageToolbar';
import PrintHidden from 'components/PrintHidden';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants';
import Layout from './Layout';
import { breakpoints, maxWidth, gutterWidth, colors } from '../theme';

export const NAME_MAP = {
  ...LISTING_TYPE_HUMAN_NAME_MAP,
  IDEALIST_DAY_EVENT: getText('event'),
  IMPORTED: getText('opportunity')
};

const getMessage = ({ listingType, isOrg, isAdmin, isGroupAdmin }) => {
  const listingTypeName = NAME_MAP[listingType];
  return isOrg
    ? sprintf(getText('Your organization posted this %(listingTypeName)s'), {
        listingTypeName
      })
    : isAdmin
    ? sprintf(getText('You are an administrator of this %(listingTypeName)s'), {
        listingTypeName
      })
    : isGroupAdmin
    ? sprintf(getText('Your group posted this %(listingTypeName)s'), {
        listingTypeName
      })
    : sprintf(getText('You posted this %(listingTypeName)s'), {
        listingTypeName
      });
};

const Body = styled.div`
  display: -ms-grid;
  display: grid;
  grid-template-columns: 100%;
  -ms-grid-columns: 100%;
  grid-template-rows: max-content;
  -ms-grid-rows: max-content;
  margin-left: auto;
  margin-right: auto;
  padding: 0 ${gutterWidth / 2}px;
  position: relative;
  max-width: ${maxWidth[3]}px;
  @media (min-width: ${breakpoints[2]}) {
    padding: 0 ${gutterWidth}px;
    grid-template-columns: 288px calc(100% - 384px);
    -ms-grid-columns: 288px 96px calc(100% - 384px);
    grid-column-gap: 96px;
  }
  @media (min-width: ${breakpoints[3]}) {
    grid-template-columns: 288px 768px;
    -ms-grid-columns: 288px 96px 768px;
    grid-column-gap: 96px;
  }
`;

const Header = styled.div`
  grid-row: 1;
  -ms-grid-row: 1;
  grid-column: 1;
  -ms-grid-column: 1;
  @media (min-width: ${breakpoints[2]}) {
    grid-column: 2;
    -ms-grid-column: 3;
  }
  ${SS.space};
`;

const Main = styled.div`
  grid-column: 1;
  -ms-grid-column: 1;
  grid-row: 3;
  -ms-grid-row: 3;
  @media (min-width: ${breakpoints[2]}) {
    grid-column: 2;
    -ms-grid-column: 3;
    grid-row: 2;
    -ms-grid-row: 2;
  }
  ${SS.space};
`;

const Sidebar = styled.div`
  background: white;
  border-radius: 4px;
  grid-row: 1 / 3;
  -ms-grid-row: 1;
  -ms-grid-row-span: 2;
  grid-column: 1;
  -ms-grid-column: 1;
  @media (max-width: ${breakpoints[2]}) {
    grid-row: 2;
    -ms-grid-row: 2;
    -ms-grid-row-span: 1;
  }
  ${SS.space};
`;

const ListingLayout = ({
  adminActions,
  toolbar,
  isOrg,
  isAdmin,
  isGroupAdmin,
  listingType,
  sidebar,
  sidebarTitle,
  sidebarImage,
  header,
  buttonBar,
  main,
  banner,
  footer,
  navSection,
  url
}: {
  adminActions?: React.Node,
  toolbar?: React.Node,
  isOrg?: boolean,
  isAdmin?: boolean,
  isGroupAdmin?: boolean,
  listingType?:
    | 'JOB'
    | 'INTERNSHIP'
    | 'VOLOP'
    | 'ORG'
    | 'GROUP'
    | 'IDEALIST_DAY_EVENT'
    | 'IMPORTED',
  sidebarTitle?: ?React.Node,
  sidebarImage: ?React.Node,
  sidebar: React.Node,
  header: React.Node,
  buttonBar?: React.Node,
  main: React.Node,
  banner?: React.Node,
  footer?: React.Node,
  navSection?: 'MAIN' | 'STAFF',
  url: Object
}) => (
  <Layout
    buttonBar={buttonBar}
    defaultSearchType={listingType === 'IMPORTED' ? 'VOLOP' : listingType}
    navSection={navSection}
    url={url}
    defaultWithSearchHero={false}
  >
    {toolbar ||
      (adminActions && (
        <PageToolbar
          centered
          message={
            listingType && listingType !== 'IMPORTED'
              ? getMessage({ listingType, isOrg, isAdmin, isGroupAdmin })
              : undefined
          }
          tools={adminActions}
        />
      ))}
    {banner}
    <Body>
      <Header
        mr={[0, '20px', '40px', '100px']}
        mt={[3, 3, 3, 4]}
        mb={[0, 0, 0, 3]}
      >
        {header}
      </Header>
      <Sidebar mt={[0, 0, 0, 4]}>
        <BodySmall lineHeight="normal">
          {sidebarImage}
          {sidebarTitle}
          {sidebar}
        </BodySmall>
      </Sidebar>
      <Main mr={[0, '20px', '40px', '100px']}>{main}</Main>
    </Body>
    {footer && (
      <PrintHidden>
        <Box
          py={[gutterWidth, null, null, gutterWidth * 2]}
          bg={colors.fog}
          mb={['-1px', null, null, -55]}
          textAlign="center"
        >
          <Box
            px={[gutterWidth / 2, null, null, gutterWidth]}
            maxWidth={maxWidth[3]}
            m="0 auto"
          >
            {footer}
          </Box>
        </Box>
      </PrintHidden>
    )}
  </Layout>
);

export default ListingLayout;
